import { Controller } from "@hotwired/stimulus";
import IndexedDB from "./indexeddb";
export default class extends Controller {
    constructor() {
        super(...arguments);
        this.vapidPublicKey = "";
        this.csrfToken = "";
        this.dbInstance = new IndexedDB();
        this.PING_INTERVAL = 10000;
    }
    async connect() {
        await this.dbInstance.initDB();
        this.registerServiceWorker();
        const vapidMetaTag = document.querySelector("meta[name='vapid-public-key']");
        this.vapidPublicKey = vapidMetaTag.getAttribute("content");
        const csrfTokenMetaTag = document.querySelector("[name=csrf-token]");
        this.csrfToken = csrfTokenMetaTag.content;
        await this.setupPeriodicSync();
    }
    async setupPeriodicSync() {
        if ("serviceWorker" in navigator) {
            try {
                const registration = await navigator.serviceWorker.ready;
                if ("periodicSync" in registration) {
                    const periodicSync = registration.periodicSync;
                    await periodicSync.register("ping-server", {
                        minInterval: this.PING_INTERVAL,
                    });
                    console.log("Periodic Sync registered for server ping");
                }
                else {
                    console.warn("periodicSync not supported — falling back to setInterval");
                    this.setupFallbackPing();
                }
            }
            catch (error) {
                console.error("Service Worker registration failed", error);
                this.setupFallbackPing();
            }
        }
    }
    setupFallbackPing() {
        // Use setInterval as fallback when periodic sync isn't available
        setInterval(() => this.ping(), this.PING_INTERVAL);
    }
    // Fallback ping method when periodic sync isn't supported
    async ping() {
        try {
            const response = await fetch("/ping", { cache: "no-store" });
            const text = await response.text();
            if (text === "pong") {
                console.log("Server is online (from fallback)");
            }
            else {
                console.warn("Unexpected response:", text);
            }
        }
        catch (error) {
            console.error("Ping failed (from fallback)", error);
        }
    }
    async registerServiceWorker() {
        if (navigator.serviceWorker) {
            try {
                await navigator.serviceWorker.register("/service-worker.js");
                console.info("Service worker registered.");
            }
            catch (error) {
                console.error("Error registering service worker: ", error);
            }
        }
    }
    async sendPush() {
        const applicationServerKey = this.urlBase64ToUint8Array(this.vapidPublicKey);
        const registration = await navigator
            .serviceWorker.ready;
        const existingSubscription = await registration.pushManager.getSubscription();
        if (existingSubscription) {
            const existingKeyBuffer = existingSubscription.options.applicationServerKey;
            // Must unsubscribe the old subscription before creating a new one.
            if (existingKeyBuffer) {
                const existingKey = new Uint8Array(existingKeyBuffer);
                const currentKey = applicationServerKey;
                const keysAreEqual = existingKey.length === currentKey.length &&
                    existingKey.every((value, index) => value === currentKey[index]);
                if (!keysAreEqual) {
                    await existingSubscription.unsubscribe();
                }
                else {
                    // Already subscribed with correct key.
                    return this.sendSubscriptionToServer(existingSubscription);
                }
            }
            else {
                // If there's no applicationServerKey on the existing subscription.
                await existingSubscription.unsubscribe();
                console.log("No key on existing subscription.");
            }
        }
        // Subscribe with new key.
        const newSubscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: applicationServerKey,
        });
        await this.sendSubscriptionToServer(newSubscription);
    }
    async sendSubscriptionToServer(subscription) {
        await fetch("/send_notification", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": this.csrfToken,
            },
            body: JSON.stringify({
                subscription: subscription,
            }),
        });
    }
    urlBase64ToUint8Array(base64str) {
        const padding = "=".repeat((4 - (base64str.length % 4)) % 4);
        const base64 = (base64str + padding).replace(/\-/g, "+").replace(/_/g, "/");
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    }
}
